import { FC } from 'react';
import { InternalOverviewResponse } from '../../types/internal';
import { InternalKPICard, InternalChartCard, InternalTableCard, InternalSectionHeader } from './index';
import { formatNumber } from '../../utils/formatters';

interface InternalEngagementSectionProps {
    // leads: InternalOverviewResponse['leads']; // Removed as unused
    emails: InternalOverviewResponse['emails'];
    posts: InternalOverviewResponse['posts'];
    periodLabel: string;
}

export const InternalEngagementSection: FC<InternalEngagementSectionProps> = ({ emails, posts, periodLabel }) => {
    // const leadsByPlatformData = (leads.byPlatform || []).filter(p => p.count > 0).slice(0, 6);

    return (
        <div className="space-y-4">
            <section>
                <InternalSectionHeader title="Engagement" />
                <div className="grid grid-cols-3 gap-3">
                    <InternalKPICard label={`Emails (${periodLabel})`} value={formatNumber(emails.inRange ?? 0)} sublabelText="Sent" tooltip="Emails sent within the selected period." />
                    <InternalKPICard label="Emails Today" value={formatNumber(emails.today ?? 0)} sublabelText="Today" tooltip="Count of emails sent since midnight." />
                    <InternalKPICard label="Total Posts" value={formatNumber(posts.total ?? 0)} sublabelText="All time" tooltip="Accumulated count of posts created." />
                </div>
            </section>

            <section className="grid grid-cols-3 gap-3">
                {/* Top Email Recipients */}
                <InternalTableCard title="Top Email Recipients" subtitle="Most contacted" description="Recipients who have received the most emails.">
                    {(emails.topRecipients || []).slice(0, 6).map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="w-4 h-4 bg-neutral-100 flex items-center justify-center text-[9px] font-medium text-neutral-500">{i + 1}</span>
                                <span className="text-[11px] text-neutral-700 truncate max-w-[120px]">{r.email}</span>
                            </div>
                            <span className="text-[11px] font-medium text-neutral-900">{r.count}</span>
                        </div>
                    ))}
                    {(emails.topRecipients || []).length === 0 && <p className="text-[11px] text-neutral-400 py-2">No data</p>}
                </InternalTableCard>

                {/* Emails by Type */}
                <InternalChartCard title="Email Types" subtitle="Distribution by category" description="Breakdown of emails by their specific type (Outreach, Reports, Bookings, etc.).">
                    {(emails.byStatus || []).length > 0 ? (
                        <div className="space-y-2">
                            {(emails.byStatus || []).map((s, i) => (
                                <div key={i} className="flex items-center justify-between text-[11px]">
                                    <span className="text-neutral-600">{s.status || 'Unknown'}</span>
                                    <span className="font-medium text-neutral-900">{s.count}</span>
                                </div>
                            ))}
                        </div>
                    ) : <p className="text-[11px] text-neutral-400">No data</p>}
                </InternalChartCard>

                {/* Posts by Status */}
                <InternalChartCard title="Content Status" subtitle="Posts breakdown" description="Current distribution of posts by status.">
                    <div className="space-y-2">
                        {(posts.byStatus || []).filter(s => s.count > 0).map((s, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px]">
                                <span className="text-neutral-600 capitalize">{s.status?.toLowerCase() || 'Unknown'}</span>
                                <span className="font-medium text-neutral-900">{s.count}</span>
                            </div>
                        ))}
                        {(posts.byStatus || []).length === 0 && <p className="text-[11px] text-neutral-400">No posts</p>}
                    </div>
                </InternalChartCard>
            </section>
        </div>
    );
};
